import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Bug, Shield, HelpCircle } from "lucide-react";
import ArcadeNav from "../arcade/ArcadeNav";
import ArcadeFooter from "../arcade/ArcadeFooter";
import useSEO from "../seo/useSEO";
import "./legal.css";

const CONTACT_EMAIL = "hello@zombiecollector3d.com";
const LAST_UPDATED = "June 26, 2026";

const CARDS = [
  {
    icon: Mail,
    title: "General questions",
    body: "Feedback, feature requests, or a friendly hello — anything that isn't a bug.",
    subject: "Hello Zombie Arcade",
  },
  {
    icon: Bug,
    title: "Bug reports",
    body: "Include your browser, device, and steps to reproduce so we can fix it fast.",
    subject: "Bug report",
  },
  {
    icon: Shield,
    title: "Privacy / data requests",
    body: "Export your data, delete your account, or ask what we hold about you.",
    subject: "Privacy request",
  },
  {
    icon: HelpCircle,
    title: "VIP or purchase help",
    body: "Refund requests, missing perks, receipt copies, or Stripe issues.",
    subject: "VIP / purchase question",
  },
];

export default function ContactPage() {
  useSEO({
    title: "Contact — Zombie Arcade",
    description:
      "Contact Zombie Arcade — bug reports, VIP support, privacy requests, and general feedback. Real email, real replies.",
    canonical: "https://zombiecollector3d.com/contact",
  });

  return (
    <div className="legal-page" data-testid="contact-page">
      <ArcadeNav />
      <main className="legal-main">
        <Link to="/" className="legal-back" data-testid="contact-back">
          <ArrowLeft size={16} /> Back to Arcade
        </Link>

        <header className="legal-hero">
          <h1>Get in touch</h1>
          <p className="subtitle">
            Real email, real replies. We aim to respond within{" "}
            <strong>1&ndash;3 business days</strong>.
          </p>
          <p className="legal-updated">Last updated: {LAST_UPDATED}</p>
        </header>

        <section className="legal-section">
          <h2>Email us</h2>
          <p>
            The fastest way to reach Zombie Arcade is by email. One human
            reads and answers every message.
          </p>
          <p style={{ fontSize: "1.15rem", marginTop: 14 }}>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-primary-email"
              style={{ fontWeight: 600 }}
            >
              {CONTACT_EMAIL}
            </a>
          </p>

          <div className="contact-methods">
            {CARDS.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="contact-card">
                  <Icon size={28} color="#7ed957" />
                  <h3>{c.title}</h3>
                  <p style={{ fontSize: "0.86rem", color: "#a298c2", margin: "4px 0 10px" }}>{c.body}</p>
                  <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(c.subject)}`}>
                    Email &rarr;
                  </a>
                </div>
              );
            })}
          </div>
        </section>

        <section className="legal-section">
          <h2>Response times</h2>
          <ul>
            <li><strong>Bug reports</strong> &mdash; usually within 24 hours; critical bugs same-day.</li>
            <li><strong>Purchase help</strong> &mdash; within 1 business day so we can investigate with Stripe.</li>
            <li><strong>Privacy / data requests</strong> &mdash; within 7 days per our{" "}
              <Link to="/privacy">Privacy Policy</Link>.</li>
            <li><strong>Everything else</strong> &mdash; 1&ndash;3 business days.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>Before you email &mdash; quick answers</h2>
          <h3>Can I get a refund for a VIP purchase?</h3>
          <p>
            All sales are final in general, but if you were charged in
            error or the service failed to deliver, email us within 14
            days and we&apos;ll investigate. See our{" "}
            <Link to="/terms">Terms of Service</Link> for details.
          </p>
          <h3>How do I delete my account?</h3>
          <p>
            Email us from the address associated with your Google account
            and ask for account deletion. We&apos;ll confirm and wipe
            everything within 7 days.
          </p>
          <h3>Can I get my in-game data as a file?</h3>
          <p>
            Yes &mdash; email us and we&apos;ll export your player profile
            (level, XP, coins, cosmetics, run history) as JSON within 7
            days at no cost.
          </p>
          <h3>Are you looking for game ideas?</h3>
          <p>
            Always. Send us a paragraph describing the game and we&apos;ll
            reply with an honest opinion. No promises we&apos;ll build it,
            but we love hearing what people want.
          </p>
        </section>

        <ArcadeFooter />
      </main>
    </div>
  );
}
